import { inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';
import { ViewLarge, ViewMedium, Viewport, ViewSmall } from 'bernie-view';

import { CurrentHotelStore } from 'stores/current-hotel-store';
import { ServerDrivenUiStore } from 'src/common/sdui/sdui-store';

import { ScrollTarget, StickyOfferModel } from 'src/common/models';
import { SduiFeature } from 'src/common/sdui/types';

import { ToolbarType, UitkOverlayToolbar, UitkToolbar } from 'uitk-react-toolbar';
import { UitkCard, UitkCardContent } from 'uitk-react-cards';

import { BackLink } from './property-details/components/back-link';
import { InformationLarge } from 'components/infosite/property-details/information.tsx';
import { NavigationRefs } from './navigation/navigation-refs';
import { PropertyBadges } from './property-details/property-badges';

export interface PropertySummaryProps {
  backToHsrUrl: string;
  useToolbar: boolean;
  goBack: () => void;
  favoriteButton?: JSX.Element;
  hotelName?: string;
  englishHotelName?: string;
  stars?: string;
  starRatingIcon?: string;
  stickyOffer?: StickyOfferModel;
  roomsAndRatesTarget?: ScrollTarget;
  currentHotel?: CurrentHotelStore;
  vipMessage?: string; // Experiment: 28909 pwa_VIP_badges
  propertyTypeBadge?: string; // Experiment: 32599 Property_Type_Badging
  sdui?: ServerDrivenUiStore;
}

export type LocalizedPropertySummaryProps = LocalizedComponentProps & PropertySummaryProps;

@withRouter
@withLocalization
@inject('sdui')
export class PropertySummary extends React.Component<LocalizedPropertySummaryProps> {
  constructor(props) {
    super(props);
  }

  public static defaultProps: Partial<LocalizedPropertySummaryProps> = {
    useToolbar: true
  };

  public render() {
    const {
      l10n,
      useToolbar,
      backToHsrUrl,
      goBack,
      favoriteButton,
      hotelName,
      englishHotelName,
      stars,
      starRatingIcon,
      stickyOffer,
      roomsAndRatesTarget,
      currentHotel,
      vipMessage,
      propertyTypeBadge,
      sdui
    } = this.props;

    const toolbarProps = {
      action: goBack,
      iconLabel: l10n.formatText('hotels.infosite.backToSearchResults.linkText'),
      type: ToolbarType.BACK
    };
    const actionLeftContentLabel = l10n.formatText('hotels.infosite.back.arrow.title');

    // When the expandedPropertySummary flag is set
    if (sdui && sdui.isFeatureActive(SduiFeature.EXPANDED_PROPERTY_SUMMARY)) {
      const navigateTabsCallback = () => {
        const tabGroup = NavigationRefs.PageTabs.current;
        if (tabGroup) {
          const accomTab = tabGroup.props.tabs.find(tab => tab.rfrr === 'Tab.Accommodation');
          const label = accomTab && accomTab.label;
          if (label) {
            const instance = tabGroup.wrappedInstance || tabGroup;
            instance.handleTabClick(label);
          }
        }
      };

      return (
        <ExpandedPropertySummary
          backToHsrUrl={backToHsrUrl}
          goBack={goBack}
          vipMessage={vipMessage}
          propertyTypeBadge={propertyTypeBadge}
          hotelName={hotelName}
          englishHotelName={englishHotelName}
          stars={stars}
          starRatingIcon={starRatingIcon}
          stickyOffer={stickyOffer}
          roomsAndRatesTarget={roomsAndRatesTarget}
          currentHotel={currentHotel}
          navigateTabs={navigateTabsCallback}
        />
      );
    }

    return (
      <Viewport>
        <ViewSmall>
          <React.Fragment>
            {useToolbar && <UitkOverlayToolbar {...toolbarProps} actionContent={favoriteButton} overlap={true} />}
            {!useToolbar && <BackLink className="all-t-padding-four" backToHsrUrl={backToHsrUrl} goBack={goBack} />}
          </React.Fragment>
        </ViewSmall>
        <ViewMedium>
          <React.Fragment>
            {useToolbar && (
              <UitkOverlayToolbar
                {...toolbarProps}
                actionContent={favoriteButton}
                actionLeftContent={actionLeftContentLabel}
                overlap={true}
              />
            )}
            {!useToolbar && <BackLink className="all-t-padding-four" backToHsrUrl={backToHsrUrl} goBack={goBack} />}
          </React.Fragment>
        </ViewMedium>
        <ViewLarge>
          <React.Fragment>
            <AbacusExperiment name="Lshop_PDP_NavBar">
              <AbacusControl>
                <React.Fragment>
                  {useToolbar && <UitkToolbar {...toolbarProps} actionLeftContent={actionLeftContentLabel} />}
                  {!useToolbar && <BackLink className="all-t-padding-three" backToHsrUrl={backToHsrUrl} goBack={goBack} />}
                </React.Fragment>
              </AbacusControl>
              <AbacusVariant bucket={1}>
                <ExpandedPropertySummary
                  backToHsrUrl={backToHsrUrl}
                  goBack={goBack}
                  vipMessage={vipMessage}
                  propertyTypeBadge={propertyTypeBadge}
                  hotelName={hotelName}
                  englishHotelName={englishHotelName}
                  stars={stars}
                  starRatingIcon={starRatingIcon}
                  stickyOffer={stickyOffer}
                  roomsAndRatesTarget={roomsAndRatesTarget}
                  currentHotel={currentHotel}
                />
              </AbacusVariant>
            </AbacusExperiment>
          </React.Fragment>
        </ViewLarge>
      </Viewport>
    );
  }
}

interface ExpandedPropertySummaryProps {
  backToHsrUrl: string;
  goBack: () => void;
  vipMessage?: string; // Experiment: 28909 pwa_VIP_badges
  propertyTypeBadge?: string; // Experiment: 32599 Property_Type_Badging
  hotelName?: string;
  englishHotelName?: string;
  stars?: string;
  starRatingIcon?: string;
  stickyOffer?: StickyOfferModel;
  roomsAndRatesTarget?: ScrollTarget;
  currentHotel?: CurrentHotelStore;
  navigateTabs?: () => any;
}

const ExpandedPropertySummary: React.FC<ExpandedPropertySummaryProps> = props => {
  const {
    backToHsrUrl,
    goBack,
    vipMessage,
    propertyTypeBadge,
    hotelName,
    englishHotelName,
    stars,
    starRatingIcon,
    stickyOffer,
    roomsAndRatesTarget,
    currentHotel,
    navigateTabs
  } = props;

  return (
    <UitkCard className="uitk-flat-border-top uitk-flat-border-bottom all-l-padding-three all-r-padding-three">
      <UitkCardContent>
        <BackLink className="all-t-padding-three" backToHsrUrl={backToHsrUrl} goBack={goBack} />
        <PropertyBadges vipMessage={vipMessage} propertyTypeBadge={propertyTypeBadge} />
        <InformationLarge
          name={hotelName}
          englishName={englishHotelName}
          stars={stars}
          starRatingIcon={starRatingIcon}
          stickyOffer={stickyOffer}
          target={roomsAndRatesTarget}
          withPrice={false}
          currentHotel={currentHotel}
          navigateTabs={navigateTabs}
        />
      </UitkCardContent>
    </UitkCard>
  );
};
